import { useEffect, useRef, useState } from 'react';
import { SearchPeople } from '../../api/api';
import { contactToGuest } from '../../utils/contacts';

// "Find person" in the add-guest form: type 2+ letters of a name (or 3+ digits
// of a number) and pick someone already known —
//   • a guest of ANY event of this organiser (admins: all; members: the ones
//     they added + Common ones, like the guest list), or
//   • a contact from the user's OWN synced phone book (Profile → Sync).
// Picking fills surname / name / mobile via onPick. Debounced; a stale answer
// never overwrites a newer one.
export function usePeopleSearch({ organiserId, userId, allGuests, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setResults([]); setLoading(false); return undefined; }
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await SearchPeople({ OrganiserId: organiserId, UserId: userId, Text: text, AllGuests: allGuests ? 1 : 0 });
        if (my !== seq.current) return;
        const guests = (r?.guests || []).map(g => ({
          key: `g:${g.Surname}|${g.GuestName}|${g.MobileNumber}`, source: 'guest',
          Surname: g.Surname || '', GuestName: g.GuestName || '', FatherHusband: '', MobileNumber: g.MobileNumber || '',
          note: g.EventName || 'Guest',
        }));
        const seen = new Set(guests.map(g => g.MobileNumber).filter(Boolean));
        const contacts = (r?.contacts || [])
          .filter(c => !seen.has(c.MobileNumber))
          .map(c => ({
            key: `c:${c.DisplayName}|${c.MobileNumber}`, source: 'contact',
            ...contactToGuest(c),
            note: Number(c.IsPrimary) === 1 ? '★ default number' : `📇 ${c.NumberLabel || 'Contact'}`,
          }));
        setResults([...guests, ...contacts]);
      } catch {
        if (my === seq.current) setResults([]);
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, organiserId, userId, allGuests]);

  const pick = (person) => { onPick(person); setQ(''); setResults([]); };
  const clear = () => { setQ(''); setResults([]); };

  return { q, setQ, results, loading, pick, clear };
}
